import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, catchError, Observable, of, shareReplay } from 'rxjs';
import { SnackBarMessage } from '../../enums/snackBarMessage';
import { SnackBarDuration } from '../../enums/snackBarDuration';
import { Faculty } from '../../../admin/data-access/models/faculties-model';
import { Department } from '../../../admin/data-access/models/departments.model';
import { UniversityData } from '../models/universityProfiles.model';

@Injectable({
  providedIn: 'root'
})
export class AcademicLookupService {
  universities$: Observable<UniversityData[]> | undefined;
  departmentsAndUniversities$: Observable<UniversityData[]> | undefined;
  departments$: Observable<Department[]> | undefined;
  faculties$: Observable<Faculty[]> | undefined;
  degrees$: Observable<string[]> | undefined;
  expenseTypes$: Observable<string[]> | undefined;
  declinedReasons$: Observable<string[]> | undefined;
  applicationsChangeFields$: Observable<string[]> | undefined;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
  ) {}

  getUniverities(): Observable<UniversityData[]> {
    if (!this.universities$) {
      this.universities$ = this.getAcademicLookupData('/api/universities', []);
    }
    return this.universities$;
  }

  refreshUniversities(): void {
    this.universities$ = this.getAcademicLookupData('/api/universities', []);
  }

  getDepartmentsAndUniversity(): Observable<UniversityData[]> {
    if (!this.departmentsAndUniversities$) {
      this.departmentsAndUniversities$ = this.getAcademicLookupData('/api/getDepartmentsAndUniversity', []);
    }
    return this.departmentsAndUniversities$;
  }

  getOnboardingDepartmentsAndUniversity(): Observable<UniversityData[]> {
  return this.getAcademicLookupData(
    '/api/getDepartmentsAndUniversity?isOnboarding=true',
    []
  );
}

  getFaculties(): Observable<Faculty[]> {
    if (!this.faculties$) {
      this.faculties$ = this.getAcademicLookupData('/api/faculties', []);
    }
    return this.faculties$;
  }

  refreshFaculties(): void {
    this.faculties$ = this.getAcademicLookupData('/api/faculties', []);
  }

  getDegrees(): Observable<string[]> {
    if (!this.degrees$) {
      this.degrees$ = this.getAcademicLookupData('/api/degrees', []);
    }
    return this.degrees$;
  }

  getDepartments(universityName:string="",facultyName:string=""): Observable<Department[]> {
    if (!this.departments$) {
      this.departments$ = this.getAcademicLookupData(`/api/universityDepartments?universityName=${universityName}&facultyName=${facultyName}`, []);
    }
    return this.departments$;
  }

  getExpenses(): Observable<string[]> {
    if (!this.expenseTypes$) {
      this.expenseTypes$ = this.getAcademicLookupData('/api/expense-types', []);
    }
    return this.expenseTypes$;
  }

  getDeclinedReasons(): Observable<string[]> {
    if (!this.declinedReasons$) {
      this.declinedReasons$ = this.getAcademicLookupData('/api/get-declined-reasons', []);
    }
    return this.declinedReasons$;
  }

  getApplicationChangeFields(): Observable<string[]> {
    if (!this.applicationsChangeFields$) {
      this.applicationsChangeFields$ = this.getAcademicLookupData('/api/application-change-fields', []);
    }
    return this.applicationsChangeFields$;
  }

  private getAcademicLookupData<T>(url: string, defaultValue: T): Observable<T> {
    return this.http.get<T>(url).pipe(
      shareReplay(1),
      catchError(() => {
        this.snackBar.open(
          SnackBarMessage.FAILURE,
          SnackBarMessage.CLOSE,
          { duration: SnackBarDuration.DURATION }
        );
        return of(defaultValue);
      })
    );
  }
}
